import { useQuery } from '@tanstack/react-query'
import type { SavedLocation } from '@/store/location'
import {
  resolveLocation,
  getForecast,
  getHourlyForecast,
  getLatestObservation,
  getActiveAlerts,
  getActiveAlertCount,
} from './client'
import type { NwsPoint } from './types'

const KEY = 'nws'

/** Resolve coords → NWS point metadata (grid, station list, city/state). */
export function usePointQuery(loc: SavedLocation | null) {
  return useQuery({
    queryKey: [KEY, 'point', loc?.lat, loc?.lon],
    queryFn: () => resolveLocation(loc!.lat, loc!.lon),
    enabled: !!loc,
    staleTime: 24 * 60 * 60 * 1000, // grid rarely changes
  })
}

export function useForecastQuery(point: NwsPoint | undefined) {
  return useQuery({
    queryKey: [KEY, 'forecast', point?.gridId, point?.gridX, point?.gridY],
    queryFn: () => getForecast(point!),
    enabled: !!point,
    staleTime: 30 * 60 * 1000,
  })
}

export function useHourlyQuery(point: NwsPoint | undefined) {
  return useQuery({
    queryKey: [KEY, 'hourly', point?.gridId, point?.gridX, point?.gridY],
    queryFn: () => getHourlyForecast(point!),
    enabled: !!point,
    staleTime: 30 * 60 * 1000,
  })
}

export function useObservationQuery(point: NwsPoint | undefined) {
  return useQuery({
    queryKey: [KEY, 'obs', point?.gridId, point?.gridX, point?.gridY],
    queryFn: () => getLatestObservation(point!),
    enabled: !!point,
    staleTime: 10 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  })
}

/** Nationwide active-alert counts for the Nationwide view. */
export function useAlertCountQuery() {
  return useQuery({
    queryKey: [KEY, 'alert-count'],
    queryFn: getActiveAlertCount,
    staleTime: 3 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}

export function useAlertsQuery(loc: SavedLocation | null) {
  return useQuery({
    queryKey: [KEY, 'alerts', loc?.lat, loc?.lon],
    queryFn: () => getActiveAlerts(loc!.lat, loc!.lon),
    enabled: !!loc,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000, // alerts are the one thing we poll briskly
  })
}
